// A textarea that grows with its content from a minimum height up to a
// capped maximum, past which it scrolls — so a long note never blows out
// the surrounding form layout.

import { useLayoutEffect, useRef } from 'react'
import type { CSSProperties } from 'react'
import { TEXTAREA_MIN_HEIGHT, TEXTAREA_MAX_HEIGHT } from '../utils'

interface Props {
  value: string
  onChange: (v: string) => void
  placeholder?: string
  disabled?: boolean
  maxLength?: number
  style?: CSSProperties
  minHeight?: number
  maxHeight?: number
  'data-livepreview-field'?: string
}

export default function AutoGrowTextarea({
  value, onChange, placeholder, disabled, maxLength, style,
  minHeight = TEXTAREA_MIN_HEIGHT, maxHeight = TEXTAREA_MAX_HEIGHT,
  ...rest
}: Props) {
  const ref = useRef<HTMLTextAreaElement>(null)

  const resize = () => {
    const el = ref.current
    if (!el) return
    // Reset first so shrinking works, then clamp to [min, max].
    el.style.height = 'auto'
    const next = Math.max(minHeight, Math.min(el.scrollHeight, maxHeight))
    el.style.height = `${next}px`
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden'
  }

  // Re-measure whenever the value changes (covers external updates such as
  // loading an existing record, not just keystrokes).
  useLayoutEffect(resize, [value, minHeight, maxHeight])

  return (
    <textarea
      ref={ref}
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      maxLength={maxLength}
      onChange={e => { onChange(e.target.value); resize() }}
      rows={2}
      style={{
        resize: 'none',
        minHeight,
        maxHeight,
        lineHeight: 1.45,
        ...style,
      }}
      {...rest}
    />
  )
}
